import { useEffect } from 'react';
import Webcam from 'react-webcam';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@emotion/react';
import usePoseDetection from './usePoseDetection';
import usePoseVisualization from './usePoseVisualization';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.5); }
  70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
`;

const sidePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.5); }
  70% { box-shadow: 0 0 0 10px rgba(255, 193, 7, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 193, 7, 0); }
`;

const turnedPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.5); }
  70% { box-shadow: 0 0 0 10px rgba(255, 152, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 152, 0, 0); }
`;

function WebcamFeed({ webcamRef, poseLandmarkerRef, canvasRef, isFullBodyVisible, handleVisibilityChange, poseType = 'unknown', isActive = true }) {
  const { checkBodyVisibility } = usePoseDetection(webcamRef, poseLandmarkerRef, handleVisibilityChange, isActive);
  const { drawPose } = usePoseVisualization(webcamRef, poseLandmarkerRef, canvasRef, isFullBodyVisible, poseType, isActive);

  // Log props for debugging
  useEffect(() => {
    console.log('WebcamFeed props:', {
      isFullBodyVisible,
      poseType,
      hasWebcamRef: !!webcamRef.current,
      hasPoseLandmarkerRef: !!poseLandmarkerRef.current,
      hasCanvasRef: !!canvasRef.current
    });
  }, [isFullBodyVisible, poseType]);

  useEffect(() => {
    const video = webcamRef.current?.video;
    if (video && canvasRef.current) {
      const updateCanvasSize = () => {
        if (video.videoWidth && video.videoHeight) {
          canvasRef.current.width = video.videoWidth;
          canvasRef.current.height = video.videoHeight;
          console.log('Canvas dimensions updated:', { width: video.videoWidth, height: video.videoHeight });
        }
      };
      updateCanvasSize();
      video.addEventListener('loadedmetadata', updateCanvasSize);
      return () => video.removeEventListener('loadedmetadata', updateCanvasSize);
    }
  }, [webcamRef, canvasRef]);

  let borderColor = 'transparent', boxShadow = 'none', animation = 'none';
  if (isActive) {
    if (isFullBodyVisible) {
      switch (poseType) {
        case 'frontal':
          borderColor = '#4caf50';
          boxShadow = '0 0 12px rgba(76, 175, 80, 0.5)';
          animation = `${pulse} 1.5s ease-in-out infinite`;
          break;
        case 'left side':
        case 'right side':
          borderColor = '#ffc107';
          boxShadow = '0 0 12px rgba(255, 193, 7, 0.5)';
          animation = `${sidePulse} 1.5s ease-in-out infinite`;
          break;
        case 'turned':
          borderColor = '#ff9800';
          boxShadow = '0 0 12px rgba(255, 152, 0, 0.5)';
          animation = `${turnedPulse} 1.5s ease-in-out infinite`;
          break;
        default:
          borderColor = '#4caf50';
          boxShadow = '0 0 12px rgba(76, 175, 80, 0.5)';
          animation = `${pulse} 1.5s ease-in-out infinite`;
      }
    } else {
      borderColor = '#ef5350';
      boxShadow = '0 0 12px rgba(239, 83, 80, 0.5)';
      animation = 'none';
    }
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '1280px',
        aspectRatio: '4 / 3',
        border: `4px solid ${borderColor}`,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow,
        animation,
        bgcolor: 'black',
        margin: '0 auto'
      }}
      role="region"
      aria-label="Webcam feed for yoga pose detection"
    >
      <Webcam
        ref={webcamRef}
        videoConstraints={{ facingMode: 'user' }}
        style={{ width: '100%', height: '100%', transform: 'scaleX(-1)' }}
      />
      <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', transform: 'scaleX(-1)' }} />

      {!isFullBodyVisible && isActive && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'url(/images/silhouette-yoga.png) no-repeat center',
            backgroundSize: 'contain',
            opacity: 0.15,
            zIndex: 1
          }}
          aria-hidden="true"
        />
      )}
    </Box>
  );
}

export default WebcamFeed;
